import type { Action } from "@/components/MenuButton";

import type { CareerPlanStatusDialogMode } from "./_CareerPlanStatusDialog";
import {
  CAREER_PLAN_STATUS,
  isPublishedCareerPlan,
  isUnpublishedCareerPlan,
  type CareerPlanStatus
} from "./careerPlanDocument";

export const CAREER_PLAN_STATUS_SNACKBAR: Record<CareerPlanStatusDialogMode, string> = {
  publish: "Career Plan published",
  unpublish: "Career Plan unpublished",
  complete: "Career Plan completed",
  delete: "Career Plan deleted"
};

export const CAREER_PLAN_DOWNLOAD_PDF_CTA = "Download PDF";
export const CAREER_PLAN_DOWNLOAD_ERROR = "We could not download this career plan. Try again.";

type CareerPlanLifecycleMode = Exclude<CareerPlanStatusDialogMode, "delete">;

const LIFECYCLE_ACTION: Record<CareerPlanLifecycleMode, { text: string; testId: string; outlined?: boolean }> = {
  publish: { text: "Publish", testId: "career-plan-publish" },
  unpublish: { text: "Unpublish", testId: "career-plan-unpublish", outlined: true },
  complete: { text: "Complete", testId: "career-plan-complete", outlined: true }
};

export function careerPlanStatusForDialogMode(mode: CareerPlanLifecycleMode): CareerPlanStatus {
  if (mode === "publish") return CAREER_PLAN_STATUS.PUBLISHED;
  if (mode === "unpublish") return CAREER_PLAN_STATUS.UNPUBLISHED;
  return CAREER_PLAN_STATUS.COMPLETED;
}

export function getCareerPlanLifecycleModes(status: CareerPlanStatus): CareerPlanLifecycleMode[] {
  const modes: CareerPlanLifecycleMode[] = [];
  if (status === CAREER_PLAN_STATUS.DRAFT || isUnpublishedCareerPlan(status)) {
    modes.push("publish");
  }
  if (isPublishedCareerPlan(status)) {
    modes.push("unpublish");
  }
  if (isPublishedCareerPlan(status) || isUnpublishedCareerPlan(status)) {
    modes.push("complete");
  }
  return modes;
}

export function careerPlanDeleteAction(
  onDelete: () => void,
  options?: { menuOnly?: boolean; disabled?: boolean }
): Action {
  return {
    text: "Delete",
    iconName: "delete",
    destructive: true,
    onClick: onDelete,
    testId: "career-plan-delete",
    disabled: options?.disabled,
    ...(options?.menuOnly ? { menuOnly: true } : {})
  };
}

export function careerPlanDownloadPdfAction(
  onDownload: () => void,
  options?: { loading?: boolean; disabled?: boolean; variant?: Action["variant"] }
): Action {
  return {
    text: CAREER_PLAN_DOWNLOAD_PDF_CTA,
    iconName: "download",
    onClick: onDownload,
    testId: "career-plan-download-pdf",
    section: "Actions",
    loading: options?.loading,
    disabled: options?.disabled,
    ...(options?.variant ? { variant: options.variant } : {})
  };
}

function careerPlanManageAction(
  mode: CareerPlanLifecycleMode,
  onClick: () => void,
  options?: { disabled?: boolean; variant?: Action["variant"] }
): Action {
  return {
    text: LIFECYCLE_ACTION[mode].text,
    section: "Manage",
    onClick,
    testId: LIFECYCLE_ACTION[mode].testId,
    disabled: options?.disabled,
    ...(options?.variant
      ? { variant: options.variant }
      : LIFECYCLE_ACTION[mode].outlined
        ? { variant: "outlined" }
        : {})
  };
}

export function getCareerPlanTableActions(
  status: CareerPlanStatus,
  handlers: Record<CareerPlanStatusDialogMode, () => void> & { downloadPdf: () => void },
  options?: { canManage?: boolean; downloading?: boolean }
): Action[] {
  const downloadAction = careerPlanDownloadPdfAction(handlers.downloadPdf, {
    loading: options?.downloading,
    disabled: options?.downloading
  });
  if (!(options?.canManage ?? true)) return [downloadAction];

  return [
    ...getCareerPlanLifecycleModes(status).map((mode) => careerPlanManageAction(mode, handlers[mode])),
    downloadAction,
    careerPlanDeleteAction(handlers.delete)
  ];
}

export function getCareerPlanDocumentClusterActions({
  status,
  canEdit,
  canDelete,
  canDownload,
  canChangeStatus,
  saving,
  downloadingPdf,
  statusDialogOpen,
  onSave,
  onDownloadPdf,
  handlers
}: {
  status: CareerPlanStatus;
  canEdit: boolean;
  canDelete: boolean;
  canDownload?: boolean;
  canChangeStatus: boolean;
  saving: boolean;
  downloadingPdf?: boolean;
  statusDialogOpen: boolean;
  onSave: () => void;
  onDownloadPdf?: () => void;
  handlers: Record<CareerPlanStatusDialogMode, () => void>;
}): Action[] | undefined {
  if (!canEdit && !canDelete && !canDownload) return undefined;

  const actions: Action[] = [];
  if (canEdit) {
    const saveInFlight = saving && !statusDialogOpen;
    actions.push({
      text: saveInFlight ? "Saving..." : "Save",
      variant: "outlined",
      disabled: saving,
      onClick: onSave,
      testId: "career-plan-save",
      iconName: saveInFlight ? "progress_activity" : undefined,
      iconClassName: saveInFlight ? "animate-spin w-6 h-6 !block" : undefined
    });
  }
  if (canEdit) {
    for (const mode of getCareerPlanLifecycleModes(status)) {
      actions.push(careerPlanManageAction(mode, handlers[mode], { disabled: saving || !canChangeStatus }));
    }
  }
  if (canDownload && onDownloadPdf) {
    actions.push(
      careerPlanDownloadPdfAction(onDownloadPdf, {
        variant: "outlined",
        loading: downloadingPdf,
        disabled: saving || downloadingPdf
      })
    );
  }
  if (canDelete) {
    actions.push(careerPlanDeleteAction(handlers.delete, { menuOnly: true, disabled: saving }));
  }
  return actions;
}
